/**
 * useWorkflowGenerationEvents
 *
 * Shared transport layer for all generation workflows (lesson, course, etc.).
 * Manages:
 *   1. Pusher real-time subscription for live progress events
 *   2. One-time historical event fetch on mount
 *   3. Periodic polling fallback as a safety net for missed Pusher events
 *   4. job_status check from poll response (catches failures with no events)
 *   5. Event deduplication via processed sequence numbers
 *
 * Uses a singleton HistoricalEventsManager per workflow type to prevent
 * duplicate fetches across React Strict Mode double-mounts.
 */

import { useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';

import {
  pollWorkflowProgress,
  type WorkflowProgressEvent,
} from '@/core/events/workflowProgressApi';
import { usePusher } from '@/shared/realtime/PusherProvider';
import { getPusherManager } from '@/modules/session/services/pusher_manager';

// ── Singleton Historical Events Manager ────────────────────────────

/**
 * Prevents duplicate historical event fetches across component
 * mount/unmount cycles (including React Strict Mode).
 * One instance per workflow type via the module-level registry.
 */
class HistoricalEventsManager {
  private loadedJobs: Set<string> = new Set();
  private loadingJobs: Set<string> = new Set();

  hasLoaded(jobId: string): boolean {
    return this.loadedJobs.has(jobId);
  }

  isLoading(jobId: string): boolean {
    return this.loadingJobs.has(jobId);
  }

  markAsLoading(jobId: string): void {
    this.loadingJobs.add(jobId);
  }

  markAsLoaded(jobId: string): void {
    this.loadedJobs.add(jobId);
    this.loadingJobs.delete(jobId);
  }

  clearLoading(jobId: string): void {
    this.loadingJobs.delete(jobId);
    this.loadedJobs.delete(jobId);
  }

  clearAll(): void {
    this.loadedJobs.clear();
    this.loadingJobs.clear();
  }
}

/** Registry of historical event managers per workflow type */
const managerRegistry = new Map<string, HistoricalEventsManager>();

function getHistoricalEventsManager(workflowType: string): HistoricalEventsManager {
  let manager = managerRegistry.get(workflowType);
  if (!manager) {
    manager = new HistoricalEventsManager();
    managerRegistry.set(workflowType, manager);
  }
  return manager;
}

/** Clear cached job state for a workflow type (e.g., on logout) */
export function clearWorkflowHistoricalEventsCache(workflowType: string): void {
  managerRegistry.get(workflowType)?.clearAll();
}

// ── Constants ───────────────────────────────────────────────────────

/** How often to poll as a fallback safety net (30 seconds) */
const POLL_INTERVAL_MS = 30_000;

/** Terminal job statuses — stop polling when reached */
const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);

// ── Hook ────────────────────────────────────────────────────────────

interface UseWorkflowGenerationEventsOptions {
  /** Workflow type identifier for the historical events manager registry */
  workflowType: string;
  /** Active generation job ID (null when not generating) */
  activeJobId: string | null;
  /** Whether generation mode is active */
  isGenerating: boolean;
  /** Number of already-completed steps (used to skip redundant history fetches) */
  completedStepCount: number;
  /** Callback invoked for each progress event (live or historical replay) */
  onEvent: (event: WorkflowProgressEvent) => void;
  /** Callback when failure is detected (from events or job_status) */
  onFailure: (message: string) => void;
}

/**
 * Subscribes to Pusher real-time events for the active job, fetches
 * historical events on mount, and runs a periodic polling fallback.
 * Each event is forwarded to onEvent(). Failures detected from either
 * progress events or job_status are forwarded to onFailure().
 */
export function useWorkflowGenerationEvents({
  workflowType,
  activeJobId,
  isGenerating,
  completedStepCount,
  onEvent,
  onFailure,
}: UseWorkflowGenerationEventsOptions): void {
  const { isInitialized: pusherReady } = usePusher();
  const pusherManager = useMemo(() => getPusherManager(), []);
  const historicalManager = useMemo(() => getHistoricalEventsManager(workflowType), [workflowType]);

  // Stable refs for callbacks to avoid stale closures in setInterval/Pusher
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const onFailureRef = useRef(onFailure);
  onFailureRef.current = onFailure;

  // Track the highest sequence number seen so far for incremental polling
  const lastSequenceRef = useRef(0);
  // Track whether failure has already been dispatched to avoid duplicates
  const failureDispatchedRef = useRef(false);
  // Track processed sequence numbers to deduplicate events across Pusher/poll
  const processedSequencesRef = useRef<Set<number>>(new Set());

  // Reset refs when job changes
  useEffect(() => {
    lastSequenceRef.current = 0;
    failureDispatchedRef.current = false;
    processedSequencesRef.current = new Set();
  }, [activeJobId]);

  useEffect(() => {
    if (!activeJobId || !isGenerating || !pusherReady) return;

    const channelName = `private-job-${activeJobId}`;
    const channel = pusherManager.subscribe(channelName);

    /**
     * Process an event only if it hasn't been seen before.
     * Events without a sequence number (e.g., keepalive) are always processed.
     */
    const processEvent = (event: WorkflowProgressEvent) => {
      if (event.sequence_number) {
        if (processedSequencesRef.current.has(event.sequence_number)) return;
        processedSequencesRef.current.add(event.sequence_number);

        if (event.sequence_number > lastSequenceRef.current) {
          lastSequenceRef.current = event.sequence_number;
        }
      }

      onEventRef.current(event);

      // If this event is a failure or cancellation, mark as dispatched
      // so the poll fallback doesn't fire onFailure again
      if (event.status === 'failed' || event.status === 'cancelled') {
        failureDispatchedRef.current = true;
      }
    };

    if (channel) {
      channel.bind('progress.update', processEvent);
    } else {
      console.warn(`[useWorkflowGenerationEvents] Failed to subscribe to Pusher channel for ${workflowType}`);
      toast('Real-time updates unavailable. Please refresh to see latest changes.', {
        duration: 6000,
      });
    }

    // ── Historical event fetch (one-time on mount) ────────────────
    const fetchHistoricalEvents = async () => {
      if (historicalManager.hasLoaded(activeJobId) && completedStepCount > 0) {
        return;
      }

      if (historicalManager.isLoading(activeJobId)) {
        return;
      }

      historicalManager.markAsLoading(activeJobId);

      try {
        const data = await pollWorkflowProgress(activeJobId, 0);
        const events = data.events || [];

        // Mark as loaded only after successful fetch
        historicalManager.markAsLoaded(activeJobId);

        let foundFailure = false;
        let failureMsg = '';

        events.forEach((event: WorkflowProgressEvent) => {
          processEvent(event);

          if (event.status === 'failed' || event.status === 'cancelled') {
            foundFailure = true;
            failureMsg = event.message || 'Generation failed';
          }
        });

        // Check job_status from poll response as a fallback.
        // Catches failures where the DB is marked FAILED but no failure
        // event was emitted (e.g., early initialization errors).
        if (!foundFailure && (data.job_status === 'failed' || data.job_status === 'cancelled')) {
          foundFailure = true;
          failureMsg = 'Generation failed';
        }

        if (foundFailure && !failureDispatchedRef.current) {
          failureDispatchedRef.current = true;
          onFailureRef.current(failureMsg);
        }
      } catch (error) {
        console.error(`[useWorkflowGenerationEvents] Failed to fetch historical events for ${workflowType}:`, error);
        historicalManager.clearLoading(activeJobId);
      }
    };

    fetchHistoricalEvents();

    // ── Periodic polling fallback ─────────────────────────────────
    // Safety net: poll every 30s to catch missed Pusher events or
    // failures that occurred after the initial historical fetch.
    const pollIntervalId = setInterval(async () => {
      try {
        const data = await pollWorkflowProgress(activeJobId, lastSequenceRef.current);

        // Process any new events missed by Pusher (deduplicated via processEvent)
        const events = data.events || [];
        events.forEach((event: WorkflowProgressEvent) => {
          processEvent(event);
        });

        // Check job_status for terminal failure or cancellation
        if (
          (data.job_status === 'failed' || data.job_status === 'cancelled') &&
          !failureDispatchedRef.current
        ) {
          failureDispatchedRef.current = true;
          const failEvent = events.find(e => e.status === 'failed' || e.status === 'cancelled');
          onFailureRef.current(failEvent?.message || 'Generation failed');
        }

        // Stop polling if job reached a terminal state
        if (TERMINAL_STATUSES.has(data.job_status)) {
          clearInterval(pollIntervalId);
        }
      } catch (error) {
        console.error(`[useWorkflowGenerationEvents] Poll fallback failed for ${workflowType}:`, error);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      if (channel) {
        channel.unbind_all();
        pusherManager.unsubscribe(channelName);
      }
      clearInterval(pollIntervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJobId, isGenerating, pusherReady]);
}
